export type VirtualKeyboardMode = "text" | "email" | "tel" | "textarea" | "number";
export type VirtualKeyboardTarget = HTMLInputElement | HTMLTextAreaElement;

const VIRTUAL_KEYBOARD_ATTRIBUTE = "data-virtual-keyboard";
export const VIRTUAL_KEYBOARD_KEEP_OPEN_ATTRIBUTE = "data-virtual-keyboard-keep-open";
export const VIRTUAL_KEYBOARD_CLEAR_LABEL_ATTRIBUTE = "data-virtual-keyboard-clear-label";
export const VIRTUAL_KEYBOARD_CLEAR_VALUE_ATTRIBUTE = "data-virtual-keyboard-clear-value";
export const VIRTUAL_KEYBOARD_CLEAR_DISABLED_ATTRIBUTE = "data-virtual-keyboard-clear-disabled";

export const getVirtualKeyboardTargetProps = (
    mode: VirtualKeyboardMode,
    enabled: boolean,
) => (
    enabled
        ? {
            [VIRTUAL_KEYBOARD_ATTRIBUTE]: mode,
            inputMode: "none" as const,
        }
        : {
            [VIRTUAL_KEYBOARD_ATTRIBUTE]: mode,
        }
);

export const findVirtualKeyboardTarget = (target: EventTarget | null): VirtualKeyboardTarget | null => {
    if (!(target instanceof Element)) return null;

    const candidate = target.closest(`[${VIRTUAL_KEYBOARD_ATTRIBUTE}]`);
    if (candidate instanceof HTMLInputElement || candidate instanceof HTMLTextAreaElement) {
        return candidate;
    }

    return null;
};

const isFocusableVirtualKeyboardTarget = (target: Element): target is VirtualKeyboardTarget => {
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) {
        return false;
    }

    if (target.disabled || target.readOnly) {
        return false;
    }

    if (target.tabIndex < 0) {
        return false;
    }

    const style = window.getComputedStyle(target);
    return style.display !== "none" && style.visibility !== "hidden";
};

export const focusNextVirtualKeyboardTarget = (target: VirtualKeyboardTarget) => {
    const focusableTargets = Array.from(
        document.querySelectorAll(`[${VIRTUAL_KEYBOARD_ATTRIBUTE}]`)
    ).filter(isFocusableVirtualKeyboardTarget);
    const currentIndex = focusableTargets.indexOf(target);
    const nextTarget = currentIndex >= 0
        ? focusableTargets[currentIndex + 1]
        : null;

    if (!nextTarget) {
        target.blur();
        return;
    }

    nextTarget.focus({ preventScroll: true });
};

export const shouldKeepVirtualKeyboardOpen = (target: EventTarget | null) => (
    target instanceof Element &&
    !!target.closest(`[${VIRTUAL_KEYBOARD_KEEP_OPEN_ATTRIBUTE}]`)
);

export const getVirtualKeyboardMode = (target: VirtualKeyboardTarget): VirtualKeyboardMode => {
    const mode = target.dataset.virtualKeyboard;
    if (mode === "email" || mode === "tel" || mode === "textarea" || mode === "number") {
        return mode;
    }

    return "text";
};

const setElementValue = (target: VirtualKeyboardTarget, nextValue: string) => {
    const prototype = target instanceof HTMLTextAreaElement
        ? HTMLTextAreaElement.prototype
        : HTMLInputElement.prototype;
    const valueSetter = Object.getOwnPropertyDescriptor(prototype, "value")?.set;

    valueSetter?.call(target, nextValue);
};

const setTargetSelection = (target: VirtualKeyboardTarget, caretPosition: number) => {
    window.requestAnimationFrame(() => {
        target.focus({ preventScroll: true });

        if (
            target instanceof HTMLTextAreaElement ||
            ["text", "search", "tel", "url", "password"].includes(target.type)
        ) {
            target.setSelectionRange(caretPosition, caretPosition);
        }
    });
};

export const syncVirtualKeyboardInput = (
    target: VirtualKeyboardTarget,
    nextValue: string,
    caretPosition = nextValue.length,
) => {
    setElementValue(target, nextValue);
    target.dispatchEvent(new Event("input", { bubbles: true }));
    setTargetSelection(target, caretPosition);
};

export const insertVirtualKeyboardText = (
    target: VirtualKeyboardTarget,
    insertedText: string,
) => {
    const { value } = target;
    const selectionStart = target.selectionStart ?? value.length;
    const selectionEnd = target.selectionEnd ?? value.length;
    const nextValue =
        value.slice(0, selectionStart) +
        insertedText +
        value.slice(selectionEnd);

    syncVirtualKeyboardInput(target, nextValue, selectionStart + insertedText.length);
};

export const backspaceVirtualKeyboardValue = (target: VirtualKeyboardTarget) => {
    const { value } = target;
    const selectionStart = target.selectionStart ?? value.length;
    const selectionEnd = target.selectionEnd ?? value.length;

    if (selectionStart !== selectionEnd) {
        const nextValue = value.slice(0, selectionStart) + value.slice(selectionEnd);
        syncVirtualKeyboardInput(target, nextValue, selectionStart);
        return;
    }

    if (selectionStart <= 0) return;

    const nextValue =
        value.slice(0, selectionStart - 1) +
        value.slice(selectionEnd);

    syncVirtualKeyboardInput(target, nextValue, selectionStart - 1);
};
